/** Upload + display dimensions per home-page banner slot (keep in sync with backend). */
/** Max raw upload before server resize/compress (see B2B_BACK_END upload.util.ts). */
export const BANNER_UPLOAD_MAX_MB = 2;
export const BANNER_UPLOAD_MAX_KB = BANNER_UPLOAD_MAX_MB * 1024;
export const BANNER_MAX_BYTES = BANNER_UPLOAD_MAX_MB * 1024 * 1024;

export const MASTER_BANNER_SPECS = {
  HOME: {
    width: 1920,
    height: 500,
    minWidth: 1000,
    minHeight: 350,
    storeMaxKb: 99,
    label: "Home Slider",
    imageRequired: true,
  },
  SPONSORED: {
    width: 1920,
    height: 302,
    minWidth: 1000,
    minHeight: 280,
    storeMaxKb: 200,
    label: "Mid Page",
    imageRequired: true,
  },
  PROMO: {
    width: 1920,
    height: 165,
    minWidth: 1000,
    minHeight: 150,
    storeMaxKb: 180,
    label: "Bottom",
    imageRequired: true,
  },
  MARKETPLACE: {
    width: 1920,
    height: 270,
    minWidth: 1000,
    minHeight: 120,
    storeMaxKb: 180,
    label: "Marketplace",
    imageRequired: false,
  },
  MARKETPLACE_SUBCATEGORY: {
    width: 1920,
    height: 270,
    minWidth: 1000,
    minHeight: 120,
    storeMaxKb: 180,
    label: "Marketplace Subcategory",
    imageRequired: false,
  },
  MARKETPLACE_SUBCATEGORY_PRODUCTS: {
    width: 1920,
    height: 270,
    minWidth: 1000,
    minHeight: 120,
    storeMaxKb: 180,
    label: "Marketplace Subcategory Products",
    imageRequired: false,
  },
  SELLER: {
    width: 1920,
    height: 500,
    minWidth: 1000,
    minHeight: 350,
    storeMaxKb: 250,
    label: "Seller",
    imageRequired: true,
  },
  BUYER: {
    width: 1920,
    height: 500,
    minWidth: 1000,
    minHeight: 350,
    storeMaxKb: 250,
    label: "Buyer",
    imageRequired: true,
  },
};

/** Home page horizontal strips — keep in sync with backend BANNER_IMAGE_SPECS. */
export const MID_PAGE_BANNER_PIXELS = { width: 1920, height: 302 };
export const BOTTOM_PROMO_BANNER_PIXELS = { width: 1920, height: 165 };

const HOME_PAGE_STRIP_TYPES = new Set(["SPONSORED", "PROMO"]);

export function isHomePageStripBannerType(bannerType) {
  return HOME_PAGE_STRIP_TYPES.has(String(bannerType || "").toUpperCase());
}

export function getBannerDisplayPixels(bannerType) {
  const spec = getBannerSpec(bannerType);
  return { width: spec.width, height: spec.height };
}

export const DEFAULT_BANNER_SPEC = MASTER_BANNER_SPECS.HOME;

export const MOBILE_BANNER_SPEC = {
  width: 768,
  height: 400,
  minWidth: 400,
  minHeight: 200,
  storeMaxKb: 120,
  label: "Mobile",
};

export function getBannerSpec(bannerType) {
  return MASTER_BANNER_SPECS[String(bannerType || "").toUpperCase()] || DEFAULT_BANNER_SPEC;
}

export function getBannerAspectRatio(bannerType) {
  const spec = getBannerSpec(bannerType);
  return `${spec.width} / ${spec.height}`;
}

export function formatBannerSizeLabel(bannerType) {
  const spec = getBannerSpec(bannerType);
  return `${spec.width} × ${spec.height} px`;
}

export function getMobileBannerAspectRatio() {
  return `${MOBILE_BANNER_SPEC.width} / ${MOBILE_BANNER_SPEC.height}`;
}

export function formatBannerUploadHint(bannerType) {
  const spec = getBannerSpec(bannerType);
  const type = String(bannerType || "").toUpperCase();
  if (isHomePageStripBannerType(type)) {
    return `Use exactly ${spec.width} × ${spec.height} px for the home page strip (full image visible, no crop). JPG, JPEG, PNG, WEBP · Max ${BANNER_UPLOAD_MAX_MB} MB · Saved as WebP ≤ ${spec.storeMaxKb} KB. Put headline text in the image; title/description below are optional.`;
  }
  return `JPG, JPEG, PNG, WEBP only · Min ${spec.minWidth} × ${spec.minHeight} px · Resized to fit ${spec.width} × ${spec.height} px · Saved as WebP ≤ ${spec.storeMaxKb} KB · Max upload ${BANNER_UPLOAD_MAX_MB} MB`;
}

export function formatMobileBannerUploadHint() {
  const spec = MOBILE_BANNER_SPEC;
  return `JPG, JPEG, PNG, WEBP only · Min ${spec.minWidth} × ${spec.minHeight} px · Resized to fit ${spec.width} × ${spec.height} px · Saved as WebP ≤ ${spec.storeMaxKb} KB · Max upload ${BANNER_UPLOAD_MAX_MB} MB`;
}
